import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Play, Check, X as XIcon, Hourglass, CheckCircle, XCircle, Stethoscope, ArrowRight, FileText, Siren, Users, WarningCircle, Clock, ArrowLineRight, Pause, PhoneCall, Monitor, Warning, ArrowsClockwise, CalendarPlus } from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "../../components/ui/popover";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatTime12 } from "@/lib/utils";
import AddWalkinDialog from "@/components/AddWalkinDialog";
import MedicalRecordPreviewModal from "@/components/MedicalRecordPreviewModal";
import BookFollowUpDialog from "@/components/BookFollowUpDialog";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { shareAppointmentOnWhatsApp } from "@/lib/whatsapp";
import { InlineLoader } from "@/components/LoadingSkeleton";

const STATUS_META = {
  waiting: { label: "Waiting", color: "#6B7F74", icon: Hourglass, bg: "#F2EFE9" },
  in_progress: { label: "In progress", color: "#C57053", icon: Stethoscope, bg: "#FBE8DF" },
  completed: { label: "Completed", color: "#224032", icon: CheckCircle, bg: "#E1ECE5" },
  cancelled: { label: "Cancelled", color: "#8A2A2A", icon: XCircle, bg: "#F5E1E1" },
  on_hold: { label: "On hold", color: "#9B59B6", icon: Hourglass, bg: "#F3E8FF" },
  no_show: { label: "No show", color: "#6B7280", icon: XCircle, bg: "#F3F4F6" },
};

export default function DoctorQueue() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [queue, setQueue] = useState({
    appointments: [],
    summary: { waiting: 0, in_progress: 0, completed: 0, cancelled: 0, total: 0 },
    avg_consult_minutes: 10,
  });
  const [emergencyRequests, setEmergencyRequests] = useState([]);
  const [queueTab, setQueueTab] = useState("today");
  const [recordDialog, setRecordDialog] = useState({ open: false, patient: null, records: [], loading: false });
  const [previewRecord, setPreviewRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [walkinOpen, setWalkinOpen] = useState(false);
  const [followUpDialog, setFollowUpDialog] = useState({ open: false, appointment: null });
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);
  const [cancelDialog, setCancelDialog] = useState({ open: false, id: null, name: "" });
  const [cancelReason, setCancelReason] = useState("");
  const [resumeDialog, setResumeDialog] = useState({ open: false, id: null, name: "" });
  const [holdDialog, setHoldDialog] = useState({ open: false, id: null, name: "" });
  const [noShowDialog, setNoShowDialog] = useState({ open: false, id: null, name: "" });
  const [skipDialog, setSkipDialog] = useState({ open: false, id: null, name: "" });
  const [emergencyCloseDialog, setEmergencyCloseDialog] = useState({ open: false });
  const [emergencyCloseForm, setEmergencyCloseForm] = useState({
    reason: "",
    message: "",
    action: "mark_reschedule_pending",
    include_in_progress: false,
    notify_patients: true,
  });
  const [emergencyCloseLoading, setEmergencyCloseLoading] = useState(false);
  const [emergencyCloseResult, setEmergencyCloseResult] = useState(null);
  const [queueAlerts, setQueueAlerts] = useState(null);
  const [selectedSession, setSelectedSession] = useState("all");
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("all");

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const todayName = dayNames[new Date().getDay()];
  const sessions = useMemo(() => {
    if (selectedLocation !== "all") {
      const loc = locations.find((l) => l.id === selectedLocation);
      if (loc) {
        const sched = loc.working_schedule?.[todayName];
        if (sched?.sessions?.length) return sched.sessions;
        if (sched?.enabled) return [{ id: "default", label: "Clinic", start_time: sched.start_time || "09:00", end_time: sched.end_time || "19:00", max_appointments: 0 }];
        return [];
      }
    }
    const sched = profile?.working_schedule?.[todayName];
    if (sched?.sessions?.length) return sched.sessions;
    if (sched?.enabled) return [{ id: "default", label: "Clinic", start_time: sched.start_time || "09:00", end_time: sched.end_time || "19:00", max_appointments: 0 }];
    return [];
  }, [profile, todayName, selectedLocation, locations]);

  useEffect(() => {
    setSelectedSession("all");
  }, [selectedLocation]);

  const load = useCallback(async () => {
    try {
      const profRes = await api.get("/doctors/me");
      setProfile(profRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        navigate("/doctor-pending", { replace: true });
        return;
      }
    }
    try {
      const locRes = await api.get("/doctors/me/locations");
      setLocations(locRes.data.locations || []);
    } catch { /* ignore */ }
    const todayParams = selectedLocation !== "all" ? { location_id: selectedLocation } : {};
    const [todayRes, emergencyRes, alertsRes] = await Promise.allSettled([
      api.get("/appointments/today", { params: todayParams }),
      api.get("/emergency-requests/doctor"),
      api.get("/appointments/queue-alerts", { params: todayParams }),
    ]);
    if (todayRes.status === "fulfilled") setQueue(todayRes.value.data);
    if (emergencyRes.status === "fulfilled") setEmergencyRequests(emergencyRes.value.data.items || []);
    if (alertsRes.status === "fulfilled") setQueueAlerts(alertsRes.value.data);
    setLoading(false);
  }, [selectedLocation, navigate]);

  useEffect(() => {
    let mounted = true;
    let timeoutId = null;
    const safeLoad = async () => {
      if (!mounted) return;
      await load();
      if (mounted) {
        timeoutId = setTimeout(safeLoad, 6000);
      }
    };
    safeLoad();
    return () => {
      mounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/appointments/${id}/status`, { status });
      toast.success("Updated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not update");
    }
  };

  const callNext = async () => {
    try {
      const params = {};
      if (selectedLocation !== "all") params.location_id = selectedLocation;
      if (selectedSession !== "all") params.session_id = selectedSession;
      const { data } = await api.post("/appointments/next", null, { params });
      if (data.started) {
        toast.success(`Now serving #${String(data.started.token_number).padStart(2, "0")} · ${data.started.patient_name}`);
      } else if (data.completed) {
        toast.success("Marked complete. No one else is waiting.");
      } else {
        toast.message("No one is waiting in the queue.");
      }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not advance");
    }
  };

  const openRecords = async (appt) => {
    setRecordDialog({ open: true, patient: appt, records: [], loading: true });
    try {
      const { data } = await api.get(`/medical-records/patient/${appt.patient_id}?appointment_id=${appt.id}`);
      setRecordDialog({ open: true, patient: appt, records: data.records || [], loading: false });
    } catch (err) {
      setRecordDialog({ open: true, patient: appt, records: [], loading: false });
      toast.error(formatApiErrorDetail(err) || "Could not load records");
    }
  };

  const shareWhatsApp = async (appt) => {
    try {
      await shareAppointmentOnWhatsApp(appt, { sourcePage: "doctor_queue" });
      toast.success("WhatsApp message opened");
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not prepare WhatsApp message");
    }
  };

  const openCancelDialog = (id, name) => {
    setCancelReason("");
    setCancelDialog({ open: true, id, name });
  };

  const confirmCancel = async () => {
    const { id } = cancelDialog;
    try {
      await api.patch(`/appointments/${id}/status`, { status: "cancelled", reason: cancelReason });
      toast.success("Cancelled");
      setCancelDialog({ open: false, id: null, name: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not cancel");
    }
  };

  const resumeHeld = async (id, newToken) => {
    try {
      await api.patch(`/appointments/${id}/resume`, { new_token: newToken });
      toast.success("Patient resumed in queue");
      setResumeDialog({ open: false, id: null, name: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not resume");
    }
  };

  const reviewEmergency = async (id, action) => {
    try {
      await api.post(`/emergency-requests/${id}/${action}`);
      toast.success(action === "approve" ? "Emergency slot approved" : "Emergency request rejected");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not update emergency request");
    }
  };

  const inProgress = queue.appointments.find((a) => a.status === "in_progress");
  const matchesSelectedSession = (a) => selectedSession === "all" || a.queue_session_id === selectedSession;
  const todayWaitingAppointments = queue.appointments.filter((a) => a.status === "waiting" && matchesSelectedSession(a));
  const heldAppointments = queue.appointments.filter((a) => a.status === "on_hold");
  const completedAppointments = queue.appointments.filter((a) => a.status === "completed");
  const displayedAppointments = (queueTab === "today"
    ? queue.appointments.filter((a) => a.status === "waiting")
    : queueTab === "hold" ? heldAppointments
    : completedAppointments
  ).filter(matchesSelectedSession);

  const consultTimer = useMemo(() => {
    if (!inProgress?.started_at) return "00:00";
    const elapsed = Math.floor((now - new Date(inProgress.started_at).getTime()) / 1000);
    const mins = Math.floor(elapsed / 60);
    const secs = elapsed % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }, [inProgress, now]);

  return (
    <div className="min-h-screen bg-background" data-testid="doctor-dashboard">
      <main className="workspace-page-flush pt-2 pb-12 sm:pt-3 sm:pb-12">
        {/* Header */}
        <div className="flex items-start sm:items-end justify-between flex-col sm:flex-row gap-4 mb-6 sm:mb-8">
          <div className="min-w-0">
            <h1 className="font-display font-bold text-foreground text-3xl sm:text-4xl lg:text-5xl tracking-tight break-words">
              {profile?.name || "Welcome"}
            </h1>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground">
              {queue.queue_date} · avg consult{" "}
              <span className="text-foreground font-semibold">~{queue.avg_consult_minutes} min</span>
              {profile?.clinic_name && <> · <span className="break-words">{profile.clinic_name}</span></>}
            </p>
          </div>
          {locations.length > 1 && (
            <div className="relative w-full sm:w-auto">
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="appearance-none rounded-full border border-border bg-card px-4 py-2 pr-8 text-sm font-medium text-foreground cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary w-full sm:w-auto"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 256 256'%3E%3Cpath fill='%236B7F74' d='M213.66 101.66l-80 80a8 8 0 0 1-11.32 0l-80-80a8 8 0 0 1 11.32-11.32L128 164.69l74.34-74.35a8 8 0 0 1 11.32 11.32z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 10px center',
                  backgroundSize: '14px',
                }}
              >
                <option value="all">All locations</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>{loc.clinic_name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white py-3 px-4 rounded-xl border border-border shadow-sm text-center">
            <p className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wider">Waiting</p>
            <span className="text-2xl sm:text-3xl font-bold text-primary" data-testid="summary-waiting">{queue.summary.waiting}</span>
          </div>
          <div className="bg-white py-3 px-4 rounded-xl border border-border shadow-sm text-center">
            <p className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wider">Avg. Wait</p>
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-2xl sm:text-3xl font-bold text-primary">{queue.avg_consult_minutes}</span>
              <span className="text-xs text-muted-foreground font-medium">min</span>
            </div>
          </div>
          <div className="bg-white py-3 px-4 rounded-xl border border-border shadow-sm text-center">
            <p className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wider">Completed</p>
            <span className="text-2xl sm:text-3xl font-bold text-primary" data-testid="summary-completed">{queue.summary.completed}</span>
          </div>
        </div>

        {/* Currently Serving Section */}
        <section className="mb-8">
          <div className="flex justify-between items-end mb-4">
            <h2 className="text-2xl font-bold text-foreground">Currently Serving</h2>
            <div className="flex gap-2">
              {profile?.tv_display_enabled && (
                <button
                  type="button"
                  onClick={() => {
                    const params = new URLSearchParams();
                    if (selectedSession !== "all") params.set("session", selectedSession);
                    if (selectedLocation !== "all") params.set("location", selectedLocation);
                    const qs = params.toString();
                    window.open(`/doctor/queue-display${qs ? `?${qs}` : ""}`, "_blank");
                  }}
                  className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
                >
                  <Monitor size={20} />
                  TV Display
                </button>
              )}
            </div>
          </div>

          <div className="bg-white p-8 rounded-xl border border-border shadow-sm" data-testid="currently-serving-card">
            {inProgress ? (
              <div className="flex flex-col lg:flex-row justify-between gap-8">
                {/* Patient Info */}
                <div className="flex gap-6 items-center">
                  <div className="bg-primary/10 text-primary w-24 h-24 rounded-lg flex flex-col items-center justify-center text-center p-2">
                    <span className="text-sm font-bold opacity-70">{inProgress.slot_start_time ? "SLOT" : "TOKEN"}</span>
                    <span className="text-xl font-black" data-testid="serving-token-number">
                      {inProgress.slot_start_time
                        ? `${inProgress.slot_start_time?.slice(0, 5)}`
                        : `#${String(inProgress.token_number).padStart(2, "0")}`}
                    </span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-3">
                      <h3 className="text-3xl font-bold text-foreground">{inProgress.patient_name}</h3>
                      {inProgress.is_follow_up && (
                        <span className="bg-primary/10 text-primary text-xs font-bold px-2 py-1 rounded flex items-center gap-1 border border-primary/20">
                          <ArrowsClockwise size={12} /> FOLLOW-UP
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-accent animate-pulse"></span>
                      <span className="text-accent font-semibold uppercase tracking-wide text-sm">Serving</span>
                    </div>
                    {inProgress.reason && (
                      <p className="text-muted-foreground mt-1">{inProgress.reason}</p>
                    )}
                  </div>
                </div>

                {/* Consult Timer */}
                <div className="text-right">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Time in Consult</p>
                  <p className="text-4xl font-mono font-bold text-primary">{consultTimer}</p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col lg:flex-row justify-between gap-8">
                <div className="flex gap-6 items-center">
                  <div className="bg-muted w-24 h-24 rounded-lg flex flex-col items-center justify-center text-center p-2">
                    <span className="text-sm font-bold text-muted-foreground opacity-70">TOKEN</span>
                    <span className="text-xl font-black text-muted-foreground">--</span>
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-3xl font-bold text-foreground">No active patient</h3>
                    <p className="text-muted-foreground">
                      {queue.summary.waiting > 0
                        ? `${queue.summary.waiting} patient${queue.summary.waiting > 1 ? "s" : ""} waiting`
                        : "No one in queue"}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-8">
              <div className="flex gap-3">
                <Button
                  onClick={callNext}
                  className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-bold hover:bg-primary/90 transition-all shadow-md active:scale-95"
                  data-testid="call-next-btn"
                  disabled={!inProgress && queue.summary.waiting === 0}
                >
                  <PhoneCall size={20} />
                  {inProgress ? "Complete & Call Next" : "Call Next Patient"}
                </Button>
                {inProgress && (
                  <Button
                    onClick={() => setHoldDialog({ open: true, id: inProgress.id, name: inProgress.patient_name })}
                    variant="outline"
                    className="flex items-center gap-2 border-2 border-border text-foreground px-6 py-3 rounded-lg font-bold hover:bg-muted transition-all active:scale-95"
                    data-testid="hold-current-btn"
                  >
                    <Pause size={20} />
                    Hold
                  </Button>
                )}
              </div>

              <div className="flex items-center gap-3">
                {inProgress && (
                  <>
                    <button
                      type="button"
                      onClick={() => openRecords(inProgress)}
                      title="Medical Records"
                      className="p-3 border border-border rounded-lg text-muted-foreground hover:bg-muted"
                    >
                      <FileText size={20} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setSkipDialog({ open: true, id: inProgress.id, name: inProgress.patient_name })}
                      title="Skip"
                      className="p-3 border border-border rounded-lg text-muted-foreground hover:bg-muted"
                    >
                      <ArrowLineRight size={20} />
                    </button>
                  </>
                )}
                {inProgress && (
                  <button
                    type="button"
                    onClick={() => openCancelDialog(inProgress.id, inProgress.patient_name)}
                    className="ml-4 text-destructive font-bold hover:text-destructive/80 flex items-center gap-1 transition-colors"
                  >
                    <XCircle size={18} />
                    Cancel Visit
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Queue Alerts */}
        {queueAlerts && (queueAlerts.running_late?.active || queueAlerts.skipped_patients?.length > 0) && (
          <section className="mb-8 space-y-3">
            {queueAlerts.running_late?.active && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
                <Clock size={20} className="text-amber-600 shrink-0 mt-0.5" weight="duotone" />
                <div className="flex-1">
                  <p className="font-semibold text-sm text-amber-800">
                    Queue is running about {queueAlerts.running_late.delay_minutes} minutes late.
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Expected ~{queueAlerts.running_late.expected_avg_minutes} min/patient · Actual ~{queueAlerts.running_late.actual_avg_minutes} min/patient
                  </p>
                </div>
              </div>
            )}
            {queueAlerts.skipped_patients?.filter((s) => s.skip_count >= 2).map((s) => (
              <div key={s.appointment_id} className="rounded-2xl border border-orange-200 bg-orange-50 p-4 flex items-start gap-3">
                <WarningCircle size={20} className="text-orange-600 shrink-0 mt-0.5" weight="duotone" />
                <div className="flex-1">
                  <p className="font-semibold text-sm text-orange-800">
                    {s.patient_name} has been skipped {s.skip_count} time{s.skip_count > 1 ? "s" : ""} today.
                  </p>
                  <div className="grid grid-cols-1 sm:flex gap-2 mt-3">
                    <button
                      onClick={async () => {
                        try {
                          await api.patch(`/appointments/${s.appointment_id}/resume`, { new_token: false });
                          toast.success(`${s.patient_name} resumed`);
                          load();
                        } catch (err) { toast.error(formatApiErrorDetail(err) || "Could not resume"); }
                      }}
                      className="text-xs font-semibold px-3 py-2 sm:py-1.5 rounded-full bg-orange-600 text-white hover:bg-orange-700"
                    >
                      <ArrowLineRight size={12} weight="bold" className="inline mr-1" /> Resume
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          await api.patch(`/appointments/${s.appointment_id}/resume`, { new_token: true });
                          toast.success(`${s.patient_name} moved to end`);
                          load();
                        } catch (err) { toast.error(formatApiErrorDetail(err) || "Could not move"); }
                      }}
                      className="text-xs font-semibold px-3 py-2 sm:py-1.5 rounded-full border border-orange-300 text-orange-700 hover:bg-orange-100"
                    >
                      <ArrowRight size={12} weight="bold" className="inline mr-1" /> Move to end
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          await api.patch(`/appointments/${s.appointment_id}/status`, { status: "cancelled", reason: "Skipped" });
                          toast.success(`${s.patient_name} cancelled`);
                          load();
                        } catch (err) { toast.error(formatApiErrorDetail(err) || "Could not cancel"); }
                      }}
                      className="text-xs font-semibold px-3 py-2 sm:py-1.5 rounded-full border border-orange-300 text-orange-700 hover:bg-orange-100"
                    >
                      <XIcon size={12} weight="bold" className="inline mr-1" /> Cancel
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </section>
        )}

        {/* Next in Queue Section */}
        <section className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-foreground">Next in Queue</h2>
            <span className="text-sm font-medium text-muted-foreground">{displayedAppointments.length} {queueTab === "today" ? "Patients Ahead" : queueTab === "hold" ? "On Hold" : "Completed Today"}</span>
          </div>

          {sessions.length > 1 && (
            <div className="mb-4 relative w-full sm:w-auto">
              <select
                value={selectedSession}
                onChange={(e) => setSelectedSession(e.target.value)}
                className="appearance-none rounded-full border border-border bg-card px-4 py-2 pr-8 text-sm font-medium text-foreground cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary w-full sm:w-auto"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 256 256'%3E%3Cpath fill='%236B7F74' d='M213.66 101.66l-80 80a8 8 0 0 1-11.32 0l-80-80a8 8 0 0 1 11.32-11.32L128 164.69l74.34-74.35a8 8 0 0 1 11.32 11.32z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 10px center',
                  backgroundSize: '14px',
                }}
              >
                <option value="all">All Sessions</option>
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label} ({formatTime12(s.start_time?.slice(0,5))}–{formatTime12(s.end_time?.slice(0,5))})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex gap-2 mb-4 -mx-3 px-3 overflow-x-auto no-scrollbar">
            <div className="inline-flex min-w-max rounded-full border border-border bg-card p-1">
              <button
                type="button"
                onClick={() => setQueueTab("today")}
                className={`text-sm px-4 py-2 rounded-full whitespace-nowrap ${queueTab === "today" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
              >
                Today ({todayWaitingAppointments.length})
              </button>
              <button
                type="button"
                onClick={() => setQueueTab("hold")}
                className={`text-sm px-4 py-2 rounded-full whitespace-nowrap ${queueTab === "hold" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                data-testid="hold-tab"
              >
                On hold ({heldAppointments.length})
              </button>
              <button
                type="button"
                onClick={() => setQueueTab("completed")}
                className={`text-sm px-4 py-2 rounded-full whitespace-nowrap ${queueTab === "completed" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                data-testid="completed-tab"
              >
                Completed ({completedAppointments.length})
              </button>
            </div>
          </div>

          {loading ? (
            <InlineLoader />
          ) : displayedAppointments.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-8 sm:p-12 text-center text-muted-foreground" data-testid="empty-queue">
              {queueTab === "hold" ? "No patients on hold." : queueTab === "completed" ? "No completed appointments today." : "No bookings for today yet."}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden" data-testid="doctor-queue">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-muted/50 border-b border-border">
                    <th className="px-6 py-4 text-xs font-bold text-muted-foreground uppercase tracking-wider">Token</th>
                    <th className="px-6 py-4 text-xs font-bold text-muted-foreground uppercase tracking-wider">Patient Name</th>
                    <th className="px-6 py-4 text-xs font-bold text-muted-foreground uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-xs font-bold text-muted-foreground uppercase tracking-wider">Wait Time</th>
                    <th className="px-6 py-4 text-xs font-bold text-muted-foreground uppercase tracking-wider text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {displayedAppointments.map((a, index) => {
                    const meta = STATUS_META[a.status];
                    const Icon = meta.icon;
                    return (
                      <tr key={a.id} className="hover:bg-muted/30 transition-colors" data-testid={`queue-row-${a.token_number}`}>
                        <td className="px-6 py-5" data-testid={`queue-token-${a.token_number}`}>
                          <div className="font-bold text-primary">#{String(a.token_number).padStart(2, "0")}</div>
                          {a.slot_start_time && (
                            <div className="text-[10px] text-muted-foreground mt-0.5 font-mono">{a.slot_start_time?.slice(0, 5)}–{a.slot_end_time?.slice(0, 5)}</div>
                          )}
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-foreground">{a.patient_name}</span>
                            {a.is_follow_up && (
                              <span className="bg-primary/10 text-primary text-[10px] font-black px-2 py-0.5 rounded uppercase">
                                FOLLOW-UP
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: meta.color }}></span>
                            <span className="text-muted-foreground font-medium">{meta.label}</span>
                          </div>
                        </td>
                        <td className="px-6 py-5 font-mono font-semibold text-muted-foreground">
                          {a.status === "waiting" && a.estimated_wait_minutes != null ? `~${a.estimated_wait_minutes}m` : "--"}
                        </td>
                        <td className="px-6 py-5 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => openRecords(a)}
                              title="Medical Records"
                              className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg"
                            >
                              <FileText size={18} />
                            </button>
                            {a.patient_phone && a.status !== "cancelled" && a.status !== "no_show" && (
                              <button
                                type="button"
                                onClick={() => shareWhatsApp(a)}
                                title="Send WhatsApp queue link"
                                className="p-2 text-[#224032] hover:bg-primary/10 rounded-lg"
                              >
                                <WhatsAppIcon size={18} />
                              </button>
                            )}
                            {a.status === "waiting" && queueTab === "today" && !inProgress && (
                              <button
                                type="button"
                                onClick={() => setStatus(a.id, "in_progress")}
                                title="Start"
                                className="p-2 text-primary hover:bg-primary/10 rounded-lg"
                                data-testid={`start-btn-${a.token_number}`}
                              >
                                <Play size={18} weight="fill" />
                              </button>
                            )}
                            {a.status === "waiting" && queueTab === "today" && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => setSkipDialog({ open: true, id: a.id, name: a.patient_name })}
                                  title="Skip"
                                  className="p-2 text-muted-foreground hover:text-amber-600 hover:bg-amber-50 rounded-lg"
                                  data-testid={`skip-btn-${a.token_number}`}
                                >
                                  <ArrowLineRight size={18} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openCancelDialog(a.id, a.patient_name)}
                                  title="Cancel"
                                  className="p-2 text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 rounded-lg"
                                >
                                  <XIcon size={18} />
                                </button>
                              </>
                            )}
                            {a.status === "on_hold" && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => setResumeDialog({ open: true, id: a.id, name: a.patient_name })}
                                  title="Resume"
                                  className="p-2 text-primary hover:bg-primary/10 rounded-lg"
                                  data-testid={`resume-btn-${a.token_number}`}
                                >
                                  <Play size={18} weight="fill" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setNoShowDialog({ open: true, id: a.id, name: a.patient_name })}
                                  title="No-show"
                                  className="p-2 text-muted-foreground hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                                >
                                  <XCircle size={18} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openCancelDialog(a.id, a.patient_name)}
                                  title="Cancel"
                                  className="p-2 text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 rounded-lg"
                                >
                                  <XIcon size={18} />
                                </button>
                              </>
                            )}
                            {a.status === "completed" && queueTab === "completed" && (
                              a.follow_up_booking ? (
                                <FollowUpBookedPill booking={a.follow_up_booking} />
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setFollowUpDialog({ open: true, appointment: a })}
                                  title="Book follow-up"
                                  className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-2 text-xs font-bold text-primary hover:bg-primary/15"
                                  data-testid={`book-follow-up-${a.token_number}`}
                                >
                                  <CalendarPlus size={15} weight="duotone" />
                                  Book follow-up
                                </button>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Emergency Requests */}
        {emergencyRequests.length > 0 && (
          <section className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Siren size={20} weight="duotone" className="text-accent" />
              <h2 className="font-display font-bold text-2xl text-foreground tracking-tight">Emergency requests</h2>
            </div>
            <div className="bg-white rounded-xl border border-border divide-y divide-border overflow-hidden shadow-sm">
              {emergencyRequests.map((item) => (
                <div key={item.id} className="px-4 sm:px-6 py-5 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-foreground text-lg">{item.patient_name}</div>
                    <div className="text-sm text-muted-foreground">{item.queue_date} · {item.reason}</div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Emergency limit: {profile?.emergency_daily_limit ?? 0}/day
                  </div>
                  <div className="grid grid-cols-2 sm:flex gap-2 w-full sm:w-auto">
                    <Button
                      onClick={() => reviewEmergency(item.id, "approve")}
                      className="rounded-lg h-10 px-4 bg-primary text-primary-foreground hover:bg-primary/90 w-full sm:w-auto"
                    >
                      <Check size={16} weight="bold" /> <span className="ml-1.5">Approve</span>
                    </Button>
                    <Button
                      onClick={() => reviewEmergency(item.id, "reject")}
                      variant="ghost"
                      className="rounded-lg h-10 px-4 text-destructive hover:text-destructive hover:bg-destructive/10 w-full sm:w-auto"
                    >
                      <XIcon size={16} weight="bold" /> <span className="ml-1.5">Reject</span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mb-8 rounded-2xl border border-destructive/20 bg-destructive/[0.03] p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="font-display font-bold text-lg text-foreground">Emergency clinic closure</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Use this when the doctor cannot continue today and appointments need cancellation or rescheduling.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setEmergencyCloseDialog({ open: true })}
              className="inline-flex items-center justify-center gap-2 bg-destructive text-destructive-foreground px-4 py-2.5 rounded-xl font-semibold hover:bg-destructive/90 transition-colors w-full sm:w-auto"
            >
              <Warning size={18} weight="fill" />
              Emergency Close
            </button>
          </div>
        </section>
      </main>

      {/* FAB */}
      <button
        type="button"
        onClick={() => setWalkinOpen(true)}
        className="fixed bottom-6 left-6 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-colors flex items-center justify-center"
        title="New appointment"
      >
        <Users size={24} weight="duotone" />
      </button>

      {/* Medical Records Dialog */}
      <Dialog open={recordDialog.open} onOpenChange={(open) => setRecordDialog((s) => ({ ...s, open }))}>
        <DialogContent className="rounded-3xl max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Medical records</DialogTitle>
            <DialogDescription>
              {recordDialog.patient?.patient_name || "Patient"} shared these documents with you.
            </DialogDescription>
          </DialogHeader>
          {recordDialog.loading ? (
            <InlineLoader message="Loading records" subtext="Preparing shared medical files" />
          ) : recordDialog.records.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-8 text-center text-muted-foreground">
              No records shared yet.
            </div>
          ) : (
            <div className="rounded-2xl border border-border bg-card divide-y divide-border overflow-hidden">
              {recordDialog.records.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setPreviewRecord(r)}
                  className="px-5 py-4 flex items-center gap-3 hover:bg-muted/40 w-full text-left"
                >
                  <FileText size={20} weight="duotone" className="text-primary" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-foreground break-words">{r.title}</div>
                    <div className="text-xs text-muted-foreground uppercase tracking-[0.16em]">{r.record_type} · {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</div>
                  </div>
                </button>
              ))}
              <MedicalRecordPreviewModal
                record={previewRecord}
                open={!!previewRecord}
                onClose={() => setPreviewRecord(null)}
                canDelete={false}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Resume Dialog */}
      <Dialog open={resumeDialog.open} onOpenChange={(open) => { if (!open) setResumeDialog({ open: false, id: null, name: "" }); }}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Resume {resumeDialog.name}</DialogTitle>
            <DialogDescription>
              Choose how to place the patient back in the queue:
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <Button
              onClick={() => resumeHeld(resumeDialog.id, false)}
              className="rounded-full h-12 bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Play size={16} weight="bold" /> <span className="ml-1.5">Same token (original position)</span>
            </Button>
            <Button
              onClick={() => resumeHeld(resumeDialog.id, true)}
              variant="outline"
              className="rounded-full h-12"
            >
              <ArrowRight size={16} weight="bold" /> <span className="ml-1.5">New token (end of queue)</span>
            </Button>
            <Button
              variant="ghost"
              onClick={() => setResumeDialog({ open: false, id: null, name: "" })}
              className="rounded-full h-12 text-muted-foreground"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hold Dialog */}
      <Dialog open={holdDialog.open} onOpenChange={(open) => { if (!open) setHoldDialog({ open: false, id: null, name: "" }); }}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Hold patient?</DialogTitle>
            <DialogDescription>
              Are you sure you want to put {holdDialog.name} on hold? They will be paused during consultation and can be resumed later.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => setHoldDialog({ open: false, id: null, name: "" })}
              className="flex-1 rounded-full h-12"
            >
              No
            </Button>
            <Button
              onClick={() => {
                const id = holdDialog.id;
                setHoldDialog({ open: false, id: null, name: "" });
                setStatus(id, "on_hold");
              }}
              className="flex-1 rounded-full h-12 bg-amber-500 text-white hover:bg-amber-600"
            >
              Yes, hold
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Skip Dialog */}
      <Dialog open={skipDialog.open} onOpenChange={(open) => { if (!open) setSkipDialog({ open: false, id: null, name: "" }); }}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Skip patient?</DialogTitle>
            <DialogDescription>
              Are you sure you want to skip {skipDialog.name}? They will be moved to On Hold and you can call them back later.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => setSkipDialog({ open: false, id: null, name: "" })}
              className="flex-1 rounded-full h-12"
            >
              No
            </Button>
            <Button
              onClick={() => {
                const id = skipDialog.id;
                setSkipDialog({ open: false, id: null, name: "" });
                setStatus(id, "on_hold");
              }}
              className="flex-1 rounded-full h-12 bg-amber-500 text-white hover:bg-amber-600"
            >
              Yes, skip
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* No-show Dialog */}
      <Dialog open={noShowDialog.open} onOpenChange={(open) => { if (!open) setNoShowDialog({ open: false, id: null, name: "" }); }}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Mark as no-show?</DialogTitle>
            <DialogDescription>
              Mark {noShowDialog.name} as no-show? This will close their appointment slot.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => setNoShowDialog({ open: false, id: null, name: "" })}
              className="flex-1 rounded-full h-12"
            >
              No
            </Button>
            <Button
              onClick={() => {
                const id = noShowDialog.id;
                setNoShowDialog({ open: false, id: null, name: "" });
                setStatus(id, "no_show");
              }}
              className="flex-1 rounded-full h-12 bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Yes, mark no-show
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialog.open} onOpenChange={(open) => { if (!open) setCancelDialog({ open: false, id: null, name: "" }); }}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Cancel appointment?</DialogTitle>
            <DialogDescription>
              This will permanently cancel {cancelDialog.name}'s appointment. This is different from skipping — a cancelled appointment cannot be resumed or called again.
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Enter cancellation reason..."
            className="w-full rounded-xl border border-input bg-background p-3 text-sm min-h-[100px] resize-none outline-none focus:ring-2 focus:ring-primary"
          />
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setCancelDialog({ open: false, id: null, name: "" })}
              className="flex-1 rounded-full h-12"
            >
              No
            </Button>
            <Button
              onClick={confirmCancel}
              className="flex-1 rounded-full h-12 bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Yes, cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Emergency Close Dialog */}
      <Dialog open={emergencyCloseDialog.open} onOpenChange={(open) => { if (!open) { setEmergencyCloseDialog({ open: false }); setEmergencyCloseResult(null); } }}>
        <DialogContent className="rounded-3xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Warning size={24} weight="fill" className="text-destructive" />
              Emergency Close
            </DialogTitle>
            <DialogDescription>
              Close the clinic for today. This will affect waiting and on-hold appointments for the selected scope.
            </DialogDescription>
          </DialogHeader>

          {emergencyCloseResult ? (
            <div className="space-y-4">
              <div className={`rounded-xl p-4 ${emergencyCloseResult.error ? "bg-destructive/10 border border-destructive/20" : "bg-primary/10 border border-primary/20"}`}>
                <p className="font-semibold text-sm">{emergencyCloseResult.message}</p>
                {emergencyCloseResult.affected_count > 0 && (
                  <p className="text-sm text-muted-foreground mt-1">{emergencyCloseResult.affected_count} appointment(s) affected</p>
                )}
                {emergencyCloseResult.blocked_new_bookings && (
                  <p className="text-sm text-muted-foreground mt-1">New online and walk-in bookings are now blocked for this scope.</p>
                )}
              </div>
              {emergencyCloseResult.details?.length > 0 && (
                <div className="max-h-48 overflow-y-auto rounded-xl border border-border divide-y divide-border">
                  {emergencyCloseResult.details.map((d, i) => (
                    <div key={i} className="px-4 py-3 text-sm flex justify-between items-center">
                      <span className="font-medium">{d.patient_name} — Token #{String(d.token_number).padStart(2, "0")}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded ${d.new_status === "reschedule_pending" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                        {d.new_status === "reschedule_pending" ? "Needs reschedule" : "Cancelled"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <Button onClick={() => { setEmergencyCloseDialog({ open: false }); setEmergencyCloseResult(null); load(); }} className="w-full rounded-full h-12">
                Done
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Reason *</label>
                <select
                  value={emergencyCloseForm.reason}
                  onChange={(e) => setEmergencyCloseForm((s) => ({ ...s, reason: e.target.value }))}
                  className="w-full rounded-xl border border-input bg-background p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select reason...</option>
                  <option value="Medical emergency">Medical emergency</option>
                  <option value="Hospital duty">Hospital duty</option>
                  <option value="Travel delay">Travel delay</option>
                  <option value="Weather/road issue">Weather/road issue</option>
                  <option value="Clinic closed">Clinic closed</option>
                  <option value="Personal emergency">Personal emergency</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-1 block">Custom message (optional)</label>
                <textarea
                  value={emergencyCloseForm.message}
                  onChange={(e) => setEmergencyCloseForm((s) => ({ ...s, message: e.target.value }))}
                  placeholder="Additional details for patients..."
                  className="w-full rounded-xl border border-input bg-background p-3 text-sm min-h-[80px] resize-none outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Action *</label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setEmergencyCloseForm((s) => ({ ...s, action: "mark_reschedule_pending" }))}
                    className={`flex-1 rounded-xl border-2 p-3 text-sm font-semibold text-left transition-colors ${emergencyCloseForm.action === "mark_reschedule_pending" ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground hover:bg-muted"}`}
                  >
                    Mark for reschedule
                    <span className="block text-xs font-normal mt-0.5 opacity-70">Patients notified to reschedule</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setEmergencyCloseForm((s) => ({ ...s, action: "cancel" }))}
                    className={`flex-1 rounded-xl border-2 p-3 text-sm font-semibold text-left transition-colors ${emergencyCloseForm.action === "cancel" ? "border-destructive bg-destructive/5 text-destructive" : "border-border text-muted-foreground hover:bg-muted"}`}
                  >
                    Cancel appointments
                    <span className="block text-xs font-normal mt-0.5 opacity-70">Permanent cancellation</span>
                  </button>
                </div>
              </div>

              {queue.appointments.some((a) => a.status === "in_progress") && (
                <label className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={emergencyCloseForm.include_in_progress}
                    onChange={(e) => setEmergencyCloseForm((s) => ({ ...s, include_in_progress: e.target.checked }))}
                    className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                  />
                  <div>
                    <span className="text-sm font-semibold text-amber-800">Include in-progress appointment</span>
                    <span className="block text-xs text-amber-700">There is currently a patient being served</span>
                  </div>
                </label>
              )}

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={emergencyCloseForm.notify_patients}
                  onChange={(e) => setEmergencyCloseForm((s) => ({ ...s, notify_patients: e.target.checked }))}
                  className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                />
                <span className="text-sm font-medium text-foreground">Notify affected patients</span>
              </label>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => { setEmergencyCloseDialog({ open: false }); setEmergencyCloseResult(null); }}
                  className="flex-1 rounded-full h-12"
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    if (!emergencyCloseForm.reason) return;
                    setEmergencyCloseLoading(true);
                    try {
                      const params = {};
                      if (selectedLocation !== "all") params.location_id = selectedLocation;
                      if (selectedSession !== "all") params.session_id = selectedSession;
                      const { data } = await api.post("/appointments/emergency-close", {
                        queue_date: queue.queue_date || new Date().toISOString().slice(0, 10),
                        ...params,
                        reason: emergencyCloseForm.reason,
                        message: emergencyCloseForm.message || undefined,
                        action: emergencyCloseForm.action,
                        include_in_progress: emergencyCloseForm.include_in_progress,
                        notify_patients: emergencyCloseForm.notify_patients,
                      });
                      setEmergencyCloseResult(data);
                    } catch (err) {
                      toast.error(formatApiErrorDetail(err) || "Emergency close failed");
                    } finally {
                      setEmergencyCloseLoading(false);
                    }
                  }}
                  disabled={!emergencyCloseForm.reason || emergencyCloseLoading}
                  className="flex-1 rounded-full h-12 bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {emergencyCloseLoading ? "Processing..." : "Confirm Close"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AddWalkinDialog open={walkinOpen} onClose={() => { setWalkinOpen(false); load(); }} />
      <BookFollowUpDialog
        open={followUpDialog.open}
        appointment={followUpDialog.appointment}
        onClose={() => setFollowUpDialog({ open: false, appointment: null })}
        onBooked={() => load()}
      />
    </div>
  );
}

function FollowUpBookedPill({ booking }) {
  const session = booking?.queue_session_label || booking?.queue_session_id || "Session";
  const slot = booking?.slot_start_time
    ? ` · ${booking.slot_start_time.slice(0, 5)}-${booking.slot_end_time?.slice(0, 5) || ""}`
    : "";
  return (
    <div className="inline-flex flex-col items-end rounded-xl bg-primary/10 px-3 py-2 text-xs text-primary">
      <span className="font-black">Follow-up booked</span>
      <span className="font-semibold text-primary/80">{booking?.queue_date || ""} · {session}{slot}</span>
    </div>
  );
}
